import { useEffect } from 'react'

export interface BreadcrumbItem {
  name: string
  url: string
}

export interface SEOConfig {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: 'website' | 'article'
  canonical?: string
  breadcrumbs?: BreadcrumbItem[]
  schema?: Record<string, unknown> | Record<string, unknown>[]
  article?: {
    publishedTime?: string
    modifiedTime?: string
    author?: string
    section?: string
    tags?: string[]
  }
}

const DEFAULT_TITLE = 'Fugue Lab | Digital Agency for Classical Music'
const DEFAULT_DESCRIPTION =
  'LA-based digital agency helping classical musicians and music organizations build beautiful websites, grow their audience, and manage their digital presence.'
const DEFAULT_IMAGE = 'https://fuguelab.com/og-image.png'
const SITE_URL = 'https://fuguelab.com'
const SCHEMA_SCRIPT_ID = 'page-json-ld'

/**
 * og:image and twitter:image must be ABSOLUTE — a crawler fetching the tag has
 * no page context to resolve `/portfolio/x.webp` against, so a relative one is
 * simply dropped and the card renders without an image.
 *
 * This is not hypothetical: portfolio entries pass `project.image`, which is a
 * site-relative path for every entry except the handful pointing at the media
 * CDN. Two of the three Graphic Design entries shipped that way (measured
 * 2026-08-21) and would not have previewed when shared. Absolutising here fixes
 * every caller at once rather than asking each one to remember.
 */
function absoluteImage(src: string): string {
  if (/^https?:\/\//.test(src)) return src
  return `${SITE_URL}${src.startsWith('/') ? '' : '/'}${src}`
}

/**
 * Hook for managing page-level SEO including meta tags, JSON-LD schemas, and breadcrumbs
 */
export function useSEO(config: SEOConfig) {
  const {
    title,
    description = DEFAULT_DESCRIPTION,
    image = DEFAULT_IMAGE,
    url,
    type = 'website',
    canonical,
    breadcrumbs,
    schema,
    article,
  } = config

  const fullTitle = title ? `${title} | Fugue Lab` : DEFAULT_TITLE
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : url ? `${SITE_URL}${url}` : undefined

  useEffect(() => {
    // Track elements we create (for cleanup)
    const createdElements: HTMLElement[] = []

    // Update document title
    document.title = fullTitle

    // Helper to update or create meta tags
    const updateMeta = (property: string, content: string, isName = false) => {
      const selector = isName ? `meta[name="${property}"]` : `meta[property="${property}"]`
      let element = document.querySelector(selector) as HTMLMetaElement | null

      if (!element) {
        element = document.createElement('meta')
        if (isName) {
          element.setAttribute('name', property)
        } else {
          element.setAttribute('property', property)
        }
        document.head.appendChild(element)
        createdElements.push(element) // Track newly created elements
      }
      element.content = content
    }

    // Helper to update or create canonical link
    let createdCanonical = false
    const updateCanonical = (href: string) => {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
      if (!link) {
        link = document.createElement('link')
        link.setAttribute('rel', 'canonical')
        document.head.appendChild(link)
        createdCanonical = true
      }
      link.href = href
    }

    // Remove any old article:tag meta elements before adding new ones
    // This prevents accumulation when navigating between blog posts
    document.querySelectorAll('meta[property^="article:tag"]').forEach((el) => el.remove())

    // Standard meta
    updateMeta('description', description, true)

    // Open Graph
    updateMeta('og:title', fullTitle)
    updateMeta('og:description', description)
    updateMeta('og:image', absoluteImage(image))
    updateMeta('og:type', type)
    if (url) {
      updateMeta('og:url', `${SITE_URL}${url}`)
    }

    // Twitter Card
    updateMeta('twitter:card', 'summary_large_image', true)
    updateMeta('twitter:title', fullTitle, true)
    updateMeta('twitter:description', description, true)
    updateMeta('twitter:image', absoluteImage(image), true)

    // Article-specific meta (for blog posts)
    if (article && type === 'article') {
      if (article.publishedTime) {
        updateMeta('article:published_time', article.publishedTime)
      }
      if (article.modifiedTime) {
        updateMeta('article:modified_time', article.modifiedTime)
      }
      if (article.author) {
        updateMeta('article:author', article.author)
      }
      if (article.section) {
        updateMeta('article:section', article.section)
      }
      article.tags?.forEach((tag, index) => {
        updateMeta(`article:tag:${index}`, tag)
      })
    }

    // Canonical URL
    if (canonicalUrl) {
      updateCanonical(canonicalUrl)
    }

    // JSON-LD Schema injection
    const schemas: Record<string, unknown>[] = []

    // Add breadcrumb schema if provided
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: `${SITE_URL}${item.url}`,
        })),
      }
      schemas.push(breadcrumbSchema)
    }

    // Add custom schema(s)
    if (schema) {
      if (Array.isArray(schema)) {
        schemas.push(...schema)
      } else {
        schemas.push(schema)
      }
    }

    // Inject or update schema script
    let scriptElement = document.getElementById(SCHEMA_SCRIPT_ID) as HTMLScriptElement | null
    if (schemas.length > 0) {
      if (!scriptElement) {
        scriptElement = document.createElement('script')
        scriptElement.id = SCHEMA_SCRIPT_ID
        scriptElement.type = 'application/ld+json'
        document.head.appendChild(scriptElement)
      }
      // If multiple schemas, wrap in @graph
      const schemaContent = schemas.length === 1 ? schemas[0] : { '@context': 'https://schema.org', '@graph': schemas }
      scriptElement.textContent = JSON.stringify(schemaContent)
    } else if (scriptElement) {
      // Remove script if no schemas
      scriptElement.remove()
    }

    // Cleanup function - remove elements we created
    return () => {
      // Remove dynamically created meta tags
      createdElements.forEach((el) => {
        if (el.parentNode) {
          el.remove()
        }
      })

      // Remove canonical link if we created it
      if (createdCanonical) {
        const canonical = document.querySelector('link[rel="canonical"]')
        if (canonical) {
          canonical.remove()
        }
      }

      // Remove JSON-LD script
      const script = document.getElementById(SCHEMA_SCRIPT_ID)
      if (script) {
        script.remove()
      }

      // Remove any article:tag meta elements
      document.querySelectorAll('meta[property^="article:tag"]').forEach((el) => el.remove())
    }
  }, [fullTitle, description, image, url, type, canonicalUrl, breadcrumbs, schema, article])
}

/**
 * Generate Article schema for blog posts
 */
export function createArticleSchema(options: {
  headline: string
  description: string
  image?: string
  datePublished: string
  dateModified?: string
  author?: string
  url: string
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: options.headline,
    description: options.description,
    image: absoluteImage(options.image || DEFAULT_IMAGE),
    datePublished: options.datePublished,
    dateModified: options.dateModified || options.datePublished,
    author: {
      '@type': 'Person',
      name: options.author || 'Garrett John Law',
      url: 'https://garrettjohnlaw.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Fugue Lab',
      logo: {
        '@type': 'ImageObject',
        url: 'https://fuguelab.com/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}${options.url}`,
    },
  }
}

/**
 * Generate Service schema
 */
export function createServiceSchema(options: {
  name: string
  description: string
  url: string
  image?: string
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: options.name,
    description: options.description,
    url: `${SITE_URL}${options.url}`,
    image: absoluteImage(options.image || DEFAULT_IMAGE),
    provider: {
      '@type': 'Organization',
      name: 'Fugue Lab',
      url: SITE_URL,
    },
  }
}

/**
 * Generate CreativeWork schema for portfolio items
 */
export function createPortfolioSchema(options: {
  name: string
  description: string
  image: string
  url: string
  clientName?: string
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: options.name,
    description: options.description,
    image: absoluteImage(options.image),
    // OMITTED when empty rather than emitted as `""`. A portfolio entry whose
    // live site no longer exists carries `url: ''`, and `"url": ""` is not a
    // weaker claim than no key — it is an invalid one, asserting the work lives
    // at the empty string.
    ...(options.url ? { url: options.url } : {}),
    creator: {
      '@type': 'Organization',
      name: 'Fugue Lab',
      url: SITE_URL,
    },
    ...(options.clientName && {
      about: {
        '@type': 'Organization',
        name: options.clientName,
      },
    }),
  }
}
